/**
 * TestModeSelector - UI component for Test Mode configuration
 *
 * Features:
 * - Tabbed configuration panel (Map, NPCs, Powerups, Food)
 * - Map shape and size selection with visual previews
 * - NPC count and personality configuration
 * - Powerup and food category toggles
 * - Shrinking mode toggle (rectangular maps only)
 * - Preset buttons for quick configuration
 * - Glassmorphism styling matching ConnectionUI
 */

import {
	MapShape,
	MapSize,
	type TestModeConfig,
	TestModePreset,
	getTestModePreset,
	cloneTestModeConfig,
	canEnableShrinking,
	POWERUP_RATE_PRESETS,
	type PowerupRatePreset,
	VISIBILITY_PRESETS,
	type VisibilityPreset,
} from '@wooorms/shared';
import { PowerupType, FoodCategory } from '@wooorms/shared';
import type { PersonalityType } from '@wooorms/shared';
import { SoundManager, SoundType } from '../audio/SoundManager';

/**
 * Tab types for test mode selector
 */
enum TestModeTab {
	MAP = 'map',
	NPCS = 'npcs',
	POWERUPS = 'powerups',
	FOOD = 'food',
	RATES = 'rates',
	VISIBILITY = 'visibility',
}

/**
 * Event handlers for TestModeSelector
 */
export interface TestModeSelectorEvents {
	onConfigChange: (config: TestModeConfig) => void;
	onStartGame: (config: TestModeConfig) => void;
	onCancel: () => void;
}

/**
 * TestModeSelector - Configuration UI for Test Mode
 */
export class TestModeSelector {
	/** Container element */
	private container: HTMLDivElement;

	/** Current configuration */
	private config: TestModeConfig;

	/** Currently active tab */
	private activeTab: TestModeTab = TestModeTab.MAP;

	/** Tab buttons */
	private tabButtons: Map<TestModeTab, HTMLButtonElement> = new Map();

	/** Tab panels */
	private tabPanels: Map<TestModeTab, HTMLDivElement> = new Map();

	/** Shrinking toggle button reference (for enabling/disabling) */
	private shrinkingToggle: HTMLButtonElement | null = null;

	/** Current preset (for highlighting) */
	private currentPreset: TestModePreset = TestModePreset.CLASSIC;

	/** Preset buttons for updating selection state */
	private presetButtons: Map<TestModePreset, HTMLButtonElement> = new Map();

	/** Event handlers */
	private events: TestModeSelectorEvents;

	constructor(events: TestModeSelectorEvents) {
		this.events = events;
		this.config = getTestModePreset(TestModePreset.CLASSIC);
		this.container = document.createElement('div');
		this.createUI();
	}

	/**
	 * Create the complete UI structure
	 */
	private createUI(): void {
		// Add CSS animations
		const style = document.createElement('style');
		style.textContent = `
			@keyframes testmode-slide-up {
				from { opacity: 0; transform: translateY(30px); }
				to { opacity: 1; transform: translateY(0); }
			}
			@keyframes testmode-tab-bounce {
				0% { transform: scale(1); }
				30% { transform: scale(1.05); }
				60% { transform: scale(0.98); }
				100% { transform: scale(1); }
			}
			@keyframes testmode-panel-fade {
				from { opacity: 0; transform: translateY(10px); }
				to { opacity: 1; transform: translateY(0); }
			}
		`;
		document.head.appendChild(style);

		// Container - Full screen overlay
		this.container.id = 'testmode-selector';
		this.container.style.cssText = `
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			display: none;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			background: radial-gradient(circle at center, rgba(10, 10, 26, 0.92), rgba(5, 5, 15, 0.98));
			backdrop-filter: blur(12px);
			-webkit-backdrop-filter: blur(12px);
			z-index: 1100;
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			overflow-y: auto;
			padding: 20px;
			box-sizing: border-box;
		`;

		// Main Card
		const card = document.createElement('div');
		card.style.cssText = `
			width: 100%;
			max-width: 650px;
			background: rgba(25, 25, 40, 0.75);
			backdrop-filter: blur(16px);
			-webkit-backdrop-filter: blur(16px);
			border: 1px solid rgba(255, 255, 255, 0.08);
			border-radius: 28px;
			padding: 28px;
			box-shadow:
				0 20px 50px rgba(0, 0, 0, 0.5),
				inset 0 1px 0 rgba(255, 255, 255, 0.1);
			display: flex;
			flex-direction: column;
			gap: 20px;
			animation: testmode-slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1);
		`;
		this.container.appendChild(card);

		// Header
		const header = this.createHeader();
		card.appendChild(header);

		// Tab Bar
		const tabBar = this.createTabBar();
		card.appendChild(tabBar);

		// Tab Panels Container
		const panelContainer = document.createElement('div');
		panelContainer.style.cssText = `
			min-height: 280px;
			position: relative;
		`;

		// Create each tab panel
		this.createMapPanel(panelContainer);
		this.createNPCPanel(panelContainer);
		this.createPowerupPanel(panelContainer);
		this.createFoodPanel(panelContainer);
		this.createRatesPanel(panelContainer);
		this.createVisibilityPanel(panelContainer);

		card.appendChild(panelContainer);

		// Preset Buttons
		const presets = this.createPresetButtons();
		card.appendChild(presets);

		// Action Buttons
		const actions = this.createActionButtons();
		card.appendChild(actions);

		document.body.appendChild(this.container);

		// Show initial tab
		this.switchTab(TestModeTab.MAP);
	}

	/**
	 * Create header section
	 */
	private createHeader(): HTMLDivElement {
		const header = document.createElement('div');
		header.style.cssText = `
			text-align: center;
			padding-bottom: 8px;
			border-bottom: 1px solid rgba(255, 255, 255, 0.08);
		`;

		const title = document.createElement('h2');
		title.textContent = 'My World';
		title.style.cssText = `
			font-size: 28px;
			font-weight: 800;
			margin: 0;
			background: linear-gradient(135deg, #4ecdc4 0%, #a78bfa 100%);
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			letter-spacing: -0.5px;
		`;
		header.appendChild(title);

		const subtitle = document.createElement('p');
		subtitle.textContent = 'Create your own custom playground';
		subtitle.style.cssText = `
			color: rgba(255, 255, 255, 0.5);
			font-size: 14px;
			margin: 6px 0 0 0;
		`;
		header.appendChild(subtitle);

		return header;
	}

	/**
	 * Create tab bar
	 */
	private createTabBar(): HTMLDivElement {
		const tabBar = document.createElement('div');
		tabBar.style.cssText = `
			display: flex;
			background: rgba(0, 0, 0, 0.25);
			padding: 5px;
			border-radius: 14px;
			gap: 6px;
		`;

		const tabs: { id: TestModeTab; label: string; icon: string }[] = [
			{ id: TestModeTab.MAP, label: 'Map', icon: '🗺️' },
			{ id: TestModeTab.NPCS, label: 'NPCs', icon: '🤖' },
			{ id: TestModeTab.POWERUPS, label: 'Powerups', icon: '⚡' },
			{ id: TestModeTab.FOOD, label: 'Food', icon: '🍎' },
			{ id: TestModeTab.RATES, label: 'Rates', icon: '📊' },
			{ id: TestModeTab.VISIBILITY, label: 'Vision', icon: '👁️' },
		];

		tabs.forEach((tab) => {
			const btn = document.createElement('button');
			btn.innerHTML = `<span style="margin-right: 6px; font-size: 16px;">${tab.icon}</span>${tab.label}`;
			btn.style.cssText = `
				flex: 1;
				padding: 12px 14px;
				border: none;
				background: transparent;
				color: rgba(255, 255, 255, 0.5);
				font-weight: 600;
				font-size: 14px;
				border-radius: 10px;
				cursor: pointer;
				transition: all 0.2s ease;
				display: flex;
				align-items: center;
				justify-content: center;
			`;

			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				this.switchTab(tab.id);
			});

			btn.addEventListener('mouseover', () => {
				if (this.activeTab !== tab.id) {
					btn.style.background = 'rgba(78, 205, 196, 0.1)';
					btn.style.color = 'rgba(255, 255, 255, 0.7)';
				}
				SoundManager.getInstance().play(SoundType.UI_HOVER);
			});

			btn.addEventListener('mouseout', () => {
				if (this.activeTab !== tab.id) {
					btn.style.background = 'transparent';
					btn.style.color = 'rgba(255, 255, 255, 0.5)';
				}
			});

			this.tabButtons.set(tab.id, btn);
			tabBar.appendChild(btn);
		});

		return tabBar;
	}

	/**
	 * Switch active tab
	 */
	private switchTab(tab: TestModeTab): void {
		this.activeTab = tab;

		// Update tab button styles
		this.tabButtons.forEach((btn, t) => {
			if (t === tab) {
				btn.style.background = 'rgba(78, 205, 196, 0.2)';
				btn.style.color = '#4ecdc4';
				btn.style.boxShadow = '0 0 0 2px rgba(78, 205, 196, 0.3)';
				btn.style.animation = 'testmode-tab-bounce 0.3s ease-out';
				setTimeout(() => { btn.style.animation = ''; }, 300);
			} else {
				btn.style.background = 'transparent';
				btn.style.color = 'rgba(255, 255, 255, 0.5)';
				btn.style.boxShadow = 'none';
			}
		});

		// Update panel visibility
		this.tabPanels.forEach((panel, t) => {
			if (t === tab) {
				panel.style.display = 'block';
				panel.style.animation = 'testmode-panel-fade 0.25s ease-out';
			} else {
				panel.style.display = 'none';
			}
		});
	}

	/**
	 * Create Map tab panel
	 */
	private createMapPanel(container: HTMLDivElement): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
			display: none;
			padding: 16px 4px;
		`;

		// Shape Selection
		const shapeSection = this.createSection('Shape');
		const shapeOptions = document.createElement('div');
		shapeOptions.style.cssText = `
			display: flex;
			gap: 12px;
		`;

		const circularBtn = this.createShapeCard('Circular', MapShape.CIRCULAR, this.config.mapShape === MapShape.CIRCULAR);
		const rectangularBtn = this.createShapeCard('Rectangular', MapShape.RECTANGULAR, this.config.mapShape === MapShape.RECTANGULAR);

		circularBtn.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.updateMapShape(MapShape.CIRCULAR, circularBtn, rectangularBtn);
		});

		rectangularBtn.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.updateMapShape(MapShape.RECTANGULAR, circularBtn, rectangularBtn);
		});

		shapeOptions.appendChild(circularBtn);
		shapeOptions.appendChild(rectangularBtn);
		shapeSection.appendChild(shapeOptions);
		panel.appendChild(shapeSection);

		// Size Selection
		const sizeSection = this.createSection('Size');
		const sizeOptions = document.createElement('div');
		sizeOptions.style.cssText = `
			display: flex;
			gap: 12px;
		`;

		const sizes: { size: MapSize; label: string; desc: string }[] = [
			{ size: MapSize.SMALL, label: 'Small', desc: 'Quick games' },
			{ size: MapSize.MEDIUM, label: 'Medium', desc: 'Balanced' },
			{ size: MapSize.LARGE, label: 'Large', desc: 'Epic battles' },
		];

		const sizeButtons: HTMLButtonElement[] = [];
		sizes.forEach((s) => {
			const btn = this.createSizeCard(s.label, s.desc, s.size, this.config.mapSize === s.size);
			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				this.config.mapSize = s.size;
				sizeButtons.forEach((b, i) => {
					this.updateCardSelection(b, sizes[i]?.size === s.size);
				});
				this.markCustomPreset();
				this.notifyConfigChange();
			});
			sizeButtons.push(btn);
			sizeOptions.appendChild(btn);
		});

		sizeSection.appendChild(sizeOptions);
		panel.appendChild(sizeSection);

		// Shrinking Mode Toggle (only for rectangular)
		const shrinkSection = this.createSection('Shrinking Zone');
		const shrinkToggle = this.createToggleButton(
			'Battle Royale Mode',
			'Zone shrinks over time (rectangular only)',
			this.config.shrinkingConfig.enabled
		);
		this.shrinkingToggle = shrinkToggle;

		shrinkToggle.addEventListener('click', () => {
			if (!canEnableShrinking(this.config)) {
				SoundManager.getInstance().play(SoundType.UI_ERROR);
				return;
			}
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.config.shrinkingConfig.enabled = !this.config.shrinkingConfig.enabled;
			this.updateToggleState(shrinkToggle, this.config.shrinkingConfig.enabled);
			this.markCustomPreset();
			this.notifyConfigChange();
		});

		shrinkSection.appendChild(shrinkToggle);
		this.updateShrinkingToggleEnabled();
		panel.appendChild(shrinkSection);

		this.tabPanels.set(TestModeTab.MAP, panel);
		container.appendChild(panel);
	}

	/**
	 * Create NPC tab panel
	 */
	private createNPCPanel(container: HTMLDivElement): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
			display: none;
			padding: 16px 4px;
		`;

		// NPC Count Slider
		const countSection = this.createSection('NPC Count');
		const sliderContainer = document.createElement('div');
		sliderContainer.style.cssText = `
			display: flex;
			align-items: center;
			gap: 16px;
		`;

		const countDisplay = document.createElement('span');
		countDisplay.textContent = String(this.config.npcConfig.count);
		countDisplay.style.cssText = `
			font-size: 32px;
			font-weight: 800;
			color: #4ecdc4;
			min-width: 50px;
			text-align: center;
		`;

		const slider = document.createElement('input');
		slider.type = 'range';
		slider.min = '0';
		slider.max = '20';
		slider.value = String(this.config.npcConfig.count);
		slider.style.cssText = `
			flex: 1;
			height: 8px;
			border-radius: 4px;
			background: rgba(255, 255, 255, 0.1);
			outline: none;
			cursor: pointer;
			-webkit-appearance: none;
		`;

		// Custom slider styling
		const sliderStyle = document.createElement('style');
		sliderStyle.textContent = `
			#testmode-selector input[type="range"]::-webkit-slider-thumb {
				-webkit-appearance: none;
				width: 24px;
				height: 24px;
				border-radius: 50%;
				background: linear-gradient(135deg, #4ecdc4, #2cb5e8);
				cursor: pointer;
				box-shadow: 0 2px 8px rgba(78, 205, 196, 0.4);
				transition: transform 0.15s ease;
			}
			#testmode-selector input[type="range"]::-webkit-slider-thumb:hover {
				transform: scale(1.1);
			}
			#testmode-selector input[type="range"]::-moz-range-thumb {
				width: 24px;
				height: 24px;
				border-radius: 50%;
				background: linear-gradient(135deg, #4ecdc4, #2cb5e8);
				cursor: pointer;
				border: none;
			}
		`;
		document.head.appendChild(sliderStyle);

		slider.addEventListener('input', () => {
			const value = parseInt(slider.value, 10);
			this.config.npcConfig.count = value;
			countDisplay.textContent = String(value);
			this.markCustomPreset();
			this.notifyConfigChange();
		});

		slider.addEventListener('change', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
		});

		sliderContainer.appendChild(countDisplay);
		sliderContainer.appendChild(slider);
		countSection.appendChild(sliderContainer);
		panel.appendChild(countSection);

		// Personality Types
		const personalitySection = this.createSection('Personalities');
		const personalityGrid = document.createElement('div');
		personalityGrid.style.cssText = `
			display: flex;
			flex-wrap: wrap;
			gap: 10px;
		`;

		const personalities: { type: PersonalityType; label: string; icon: string; color: string }[] = [
			{ type: 'Collector', label: 'Collector', icon: '🌱', color: '#88ccff' },
			{ type: 'Hunter', label: 'Hunter', icon: '⚔️', color: '#ffcc00' },
			{ type: 'Apex', label: 'Apex', icon: '🔥', color: '#ff6600' },
		];

		personalities.forEach((p) => {
			const isEnabled = this.config.npcConfig.enabledPersonalities.has(p.type);
			const btn = this.createCheckboxButton(p.icon, p.label, isEnabled, p.color);

			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				if (this.config.npcConfig.enabledPersonalities.has(p.type)) {
					this.config.npcConfig.enabledPersonalities.delete(p.type);
					this.updateCheckboxState(btn, false);
				} else {
					this.config.npcConfig.enabledPersonalities.add(p.type);
					this.updateCheckboxState(btn, true);
				}
				this.markCustomPreset();
				this.notifyConfigChange();
			});

			personalityGrid.appendChild(btn);
		});

		personalitySection.appendChild(personalityGrid);
		panel.appendChild(personalitySection);

		// Leviathan Toggle
		const leviathanSection = this.createSection('Boss');
		const leviathanToggle = this.createToggleButton(
			'LEVIATHAN',
			'Spawn the legendary boss AI',
			this.config.npcConfig.allowLeviathan
		);

		leviathanToggle.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.config.npcConfig.allowLeviathan = !this.config.npcConfig.allowLeviathan;
			this.updateToggleState(leviathanToggle, this.config.npcConfig.allowLeviathan);
			this.markCustomPreset();
			this.notifyConfigChange();
		});

		leviathanSection.appendChild(leviathanToggle);
		panel.appendChild(leviathanSection);

		this.tabPanels.set(TestModeTab.NPCS, panel);
		container.appendChild(panel);
	}

	/**
	 * Create Powerup tab panel
	 */
	private createPowerupPanel(container: HTMLDivElement): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
			display: none;
			padding: 16px 4px;
		`;

		const section = this.createSection('Enabled Powerups');
		const grid = document.createElement('div');
		grid.style.cssText = `
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
			gap: 10px;
		`;

		const powerups: { type: PowerupType; label: string; icon: string; color: string }[] = [
			{ type: PowerupType.MULTIPLIER_2X, label: '2x Score', icon: '2x', color: '#00bcd4' },
			{ type: PowerupType.MULTIPLIER_4X, label: '4x Score', icon: '4x', color: '#00bcd4' },
			{ type: PowerupType.MULTIPLIER_6X, label: '6x Score', icon: '6x', color: '#00bcd4' },
			{ type: PowerupType.MAGNET, label: 'Magnet', icon: '🧲', color: '#9c27b0' },
			{ type: PowerupType.SPEED, label: 'Speed', icon: '💨', color: '#ff9800' },
			{ type: PowerupType.AGILITY, label: 'Agility', icon: '🔄', color: '#4caf50' },
			{ type: PowerupType.VISION, label: 'Vision', icon: '👁️', color: '#03a9f4' },
		];

		powerups.forEach((p) => {
			const isEnabled = this.config.enabledPowerups.has(p.type);
			const btn = this.createCheckboxButton(p.icon, p.label, isEnabled, p.color);

			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				if (this.config.enabledPowerups.has(p.type)) {
					this.config.enabledPowerups.delete(p.type);
					this.updateCheckboxState(btn, false);
				} else {
					this.config.enabledPowerups.add(p.type);
					this.updateCheckboxState(btn, true);
				}
				this.markCustomPreset();
				this.notifyConfigChange();
			});

			grid.appendChild(btn);
		});

		section.appendChild(grid);
		panel.appendChild(section);

		// Quick select buttons
		const quickSection = document.createElement('div');
		quickSection.style.cssText = `
			display: flex;
			gap: 10px;
			margin-top: 16px;
		`;

		const allBtn = this.createSmallButton('Enable All');
		allBtn.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			powerups.forEach((p) => this.config.enabledPowerups.add(p.type));
			this.refreshPowerupPanel();
			this.markCustomPreset();
			this.notifyConfigChange();
		});

		const noneBtn = this.createSmallButton('Disable All');
		noneBtn.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.config.enabledPowerups.clear();
			this.refreshPowerupPanel();
			this.markCustomPreset();
			this.notifyConfigChange();
		});

		quickSection.appendChild(allBtn);
		quickSection.appendChild(noneBtn);
		section.appendChild(quickSection);

		this.tabPanels.set(TestModeTab.POWERUPS, panel);
		container.appendChild(panel);
	}

	/**
	 * Refresh powerup panel checkboxes
	 */
	private refreshPowerupPanel(): void {
		const panel = this.tabPanels.get(TestModeTab.POWERUPS);
		if (!panel) return;

		const buttons = panel.querySelectorAll('button[data-powerup]');
		buttons.forEach((btn) => {
			const type = btn.getAttribute('data-powerup') as PowerupType;
			if (type) {
				this.updateCheckboxState(btn as HTMLButtonElement, this.config.enabledPowerups.has(type));
			}
		});
	}

	/**
	 * Create Food tab panel
	 */
	private createFoodPanel(container: HTMLDivElement): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
			display: none;
			padding: 16px 4px;
		`;

		const section = this.createSection('Food Categories');
		const grid = document.createElement('div');
		grid.style.cssText = `
			display: flex;
			flex-wrap: wrap;
			gap: 12px;
		`;

		const categories: { type: FoodCategory; label: string; icon: string; desc: string; color: string }[] = [
			{ type: FoodCategory.JUNK, label: 'Junk Food', icon: '🍬', desc: 'Low value', color: '#ff69b4' },
			{ type: FoodCategory.REGULAR, label: 'Regular', icon: '🍳', desc: 'Medium value', color: '#ffd700' },
			{ type: FoodCategory.HEALTHY, label: 'Healthy', icon: '🥗', desc: 'High value', color: '#32cd32' },
		];

		categories.forEach((c) => {
			const isEnabled = this.config.enabledFoodCategories.has(c.type);
			const btn = this.createFoodCategoryCard(c.icon, c.label, c.desc, isEnabled, c.color);

			btn.addEventListener('click', () => {
				// Ensure at least one category is always enabled
				if (this.config.enabledFoodCategories.has(c.type) && this.config.enabledFoodCategories.size === 1) {
					SoundManager.getInstance().play(SoundType.UI_ERROR);
					return;
				}

				SoundManager.getInstance().play(SoundType.UI_CLICK);
				if (this.config.enabledFoodCategories.has(c.type)) {
					this.config.enabledFoodCategories.delete(c.type);
					this.updateFoodCardState(btn, false);
				} else {
					this.config.enabledFoodCategories.add(c.type);
					this.updateFoodCardState(btn, true);
				}
				this.markCustomPreset();
				this.notifyConfigChange();
			});

			grid.appendChild(btn);
		});

		section.appendChild(grid);
		panel.appendChild(section);

		// Info text
		const info = document.createElement('p');
		info.textContent = 'At least one food category must be enabled.';
		info.style.cssText = `
			color: rgba(255, 255, 255, 0.4);
			font-size: 12px;
			margin-top: 12px;
			font-style: italic;
		`;
		section.appendChild(info);

		this.tabPanels.set(TestModeTab.FOOD, panel);
		container.appendChild(panel);
	}

	/**
	 * Create Rates tab panel (powerup spawn rates)
	 */
	private createRatesPanel(container: HTMLDivElement): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
			display: none;
			padding: 16px 4px;
		`;

		const section = this.createSection('Powerup Spawn Rate');
		const grid = document.createElement('div');
		grid.style.cssText = `
			display: flex;
			flex-direction: column;
			gap: 10px;
		`;

		// Get all presets
		const presets = Object.values(POWERUP_RATE_PRESETS);
		const rateButtons: HTMLButtonElement[] = [];

		presets.forEach((preset) => {
			const isSelected = this.config.powerupRatePreset.id === preset.id;
			const btn = this.createRatePresetCard(preset, isSelected);

			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				this.config.powerupRatePreset = { ...preset };
				rateButtons.forEach((b, i) => {
					const p = presets[i];
					if (p) {
						this.updateRateCardSelection(b, p.id === preset.id);
					}
				});
				this.markCustomPreset();
				this.notifyConfigChange();
			});

			rateButtons.push(btn);
			grid.appendChild(btn);
		});

		section.appendChild(grid);
		panel.appendChild(section);

		// Info text
		const info = document.createElement('p');
		info.innerHTML = `
			<strong>Spawn Rate</strong> = powerups spawned per second<br>
			<strong>Max Count</strong> = maximum powerups in the world
		`;
		info.style.cssText = `
			color: rgba(255, 255, 255, 0.4);
			font-size: 12px;
			margin-top: 16px;
			line-height: 1.6;
		`;
		section.appendChild(info);

		this.tabPanels.set(TestModeTab.RATES, panel);
		container.appendChild(panel);
	}

	/**
	 * Create Visibility tab panel (fog of war presets)
	 */
	private createVisibilityPanel(container: HTMLDivElement): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
			display: none;
			padding: 16px 4px;
		`;

		const section = this.createSection('Visibility Range');
		const grid = document.createElement('div');
		grid.style.cssText = `
			display: flex;
			flex-direction: column;
			gap: 10px;
		`;

		// Get all visibility presets
		const presets = Object.values(VISIBILITY_PRESETS);
		const visibilityButtons: HTMLButtonElement[] = [];

		presets.forEach((preset) => {
			const isSelected = this.config.visibilityPreset.id === preset.id;
			const btn = this.createVisibilityPresetCard(preset, isSelected);

			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				this.config.visibilityPreset = { ...preset };
				visibilityButtons.forEach((b, i) => {
					const p = presets[i];
					if (p) {
						this.updateVisibilityCardSelection(b, p.id === preset.id);
					}
				});
				this.markCustomPreset();
				this.notifyConfigChange();
			});

			visibilityButtons.push(btn);
			grid.appendChild(btn);
		});

		section.appendChild(grid);
		panel.appendChild(section);

		// Info text
		const info = document.createElement('p');
		info.innerHTML = `
			<strong>Visibility</strong> affects how far you can see around your worm.<br>
			Lower visibility makes stealth gameplay more viable.
		`;
		info.style.cssText = `
			color: rgba(255, 255, 255, 0.4);
			font-size: 12px;
			margin-top: 16px;
			line-height: 1.6;
		`;
		section.appendChild(info);

		this.tabPanels.set(TestModeTab.VISIBILITY, panel);
		container.appendChild(panel);
	}

	/**
	 * Create visibility preset card
	 */
	private createVisibilityPresetCard(preset: VisibilityPreset, selected: boolean): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.dataset.visibilityPreset = preset.id;

		// Color based on visibility level
		const multiplier = preset.radiusMultiplier;
		let color = '#4ecdc4';
		let icon = '👁️';
		if (preset.fogDisabled) {
			color = '#ffd700';
			icon = '🌟';
		} else if (multiplier >= 2) {
			color = '#95e1d3';
			icon = '🦅';
		} else if (multiplier >= 1) {
			color = '#4ecdc4';
			icon = '👁️';
		} else if (multiplier >= 0.5) {
			color = '#ff9800';
			icon = '🌫️';
		} else {
			color = '#ff6b6b';
			icon = '🌑';
		}

		const displayMultiplier = preset.fogDisabled ? '∞' : `${multiplier}x`;

		btn.innerHTML = `
			<div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
				<div style="display: flex; align-items: center; gap: 12px;">
					<div style="
						width: 40px;
						height: 40px;
						border-radius: 10px;
						background: ${color}20;
						border: 2px solid ${color};
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 20px;
					">${icon}</div>
					<div style="text-align: left;">
						<div style="font-weight: 600; font-size: 14px;">${preset.name}</div>
						<div style="font-size: 11px; color: rgba(255, 255, 255, 0.4);">${preset.description}</div>
					</div>
				</div>
				<div style="
					font-size: 14px;
					font-weight: 700;
					color: ${color};
					min-width: 40px;
					text-align: right;
				">${displayMultiplier}</div>
			</div>
		`;

		btn.style.cssText = `
			width: 100%;
			padding: 12px 16px;
			border-radius: 12px;
			border: 2px solid ${selected ? color : 'rgba(255, 255, 255, 0.1)'};
			background: ${selected ? color + '15' : 'rgba(0, 0, 0, 0.2)'};
			color: #fff;
			cursor: pointer;
			transition: all 0.2s ease;
		`;

		btn.addEventListener('mouseover', () => {
			btn.style.background = color + '20';
			btn.style.borderColor = color + '80';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			const isSelected = this.config.visibilityPreset.id === preset.id;
			btn.style.background = isSelected ? color + '15' : 'rgba(0, 0, 0, 0.2)';
			btn.style.borderColor = isSelected ? color : 'rgba(255, 255, 255, 0.1)';
		});

		return btn;
	}

	/**
	 * Update visibility card selection state
	 */
	private updateVisibilityCardSelection(btn: HTMLButtonElement, selected: boolean): void {
		const presetId = btn.dataset.visibilityPreset;
		const preset = Object.values(VISIBILITY_PRESETS).find(p => p.id === presetId);
		if (!preset) return;

		const multiplier = preset.radiusMultiplier;
		let color = '#4ecdc4';
		if (preset.fogDisabled) {
			color = '#ffd700';
		} else if (multiplier >= 2) {
			color = '#95e1d3';
		} else if (multiplier >= 1) {
			color = '#4ecdc4';
		} else if (multiplier >= 0.5) {
			color = '#ff9800';
		} else {
			color = '#ff6b6b';
		}

		btn.style.borderColor = selected ? color : 'rgba(255, 255, 255, 0.1)';
		btn.style.background = selected ? color + '15' : 'rgba(0, 0, 0, 0.2)';
	}

	/**
	 * Create rate preset card
	 */
	private createRatePresetCard(preset: PowerupRatePreset, selected: boolean): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.dataset.ratePreset = preset.id;

		// Color based on intensity
		const intensity = preset.spawnRate;
		let color = '#4ecdc4';
		if (intensity >= 4) color = '#ff6b6b';
		else if (intensity >= 2) color = '#ffd700';
		else if (intensity >= 1) color = '#4ecdc4';
		else color = '#95e1d3';

		btn.innerHTML = `
			<div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
				<div style="display: flex; align-items: center; gap: 12px;">
					<div style="
						width: 40px;
						height: 40px;
						border-radius: 10px;
						background: ${color}20;
						border: 2px solid ${color};
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 18px;
						font-weight: 700;
						color: ${color};
					">${preset.spawnRate}x</div>
					<div style="text-align: left;">
						<div style="font-weight: 600; font-size: 14px;">${preset.name}</div>
						<div style="font-size: 11px; color: rgba(255, 255, 255, 0.4);">${preset.description}</div>
					</div>
				</div>
				<div style="text-align: right; font-size: 11px; color: rgba(255, 255, 255, 0.4);">
					Max: ${preset.maxCount}
				</div>
			</div>
		`;

		btn.style.cssText = `
			width: 100%;
			padding: 12px 16px;
			border-radius: 12px;
			border: 2px solid ${selected ? color : 'rgba(255, 255, 255, 0.1)'};
			background: ${selected ? color + '15' : 'rgba(0, 0, 0, 0.2)'};
			color: #fff;
			cursor: pointer;
			transition: all 0.2s ease;
		`;

		btn.addEventListener('mouseover', () => {
			btn.style.background = color + '20';
			btn.style.borderColor = color + '80';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			const isSelected = this.config.powerupRatePreset.id === preset.id;
			btn.style.background = isSelected ? color + '15' : 'rgba(0, 0, 0, 0.2)';
			btn.style.borderColor = isSelected ? color : 'rgba(255, 255, 255, 0.1)';
		});

		return btn;
	}

	/**
	 * Update rate card selection state
	 */
	private updateRateCardSelection(btn: HTMLButtonElement, selected: boolean): void {
		const presetId = btn.dataset.ratePreset;
		const preset = Object.values(POWERUP_RATE_PRESETS).find(p => p.id === presetId);
		if (!preset) return;

		const intensity = preset.spawnRate;
		let color = '#4ecdc4';
		if (intensity >= 4) color = '#ff6b6b';
		else if (intensity >= 2) color = '#ffd700';
		else if (intensity >= 1) color = '#4ecdc4';
		else color = '#95e1d3';

		btn.style.borderColor = selected ? color : 'rgba(255, 255, 255, 0.1)';
		btn.style.background = selected ? color + '15' : 'rgba(0, 0, 0, 0.2)';
	}

	/**
	 * Create preset buttons section
	 */
	private createPresetButtons(): HTMLDivElement {
		const section = document.createElement('div');
		section.style.cssText = `
			padding-top: 16px;
			border-top: 1px solid rgba(255, 255, 255, 0.08);
		`;

		const label = document.createElement('div');
		label.textContent = 'QUICK PRESETS';
		label.style.cssText = `
			font-size: 11px;
			font-weight: 700;
			color: rgba(255, 255, 255, 0.4);
			letter-spacing: 1.5px;
			margin-bottom: 12px;
		`;
		section.appendChild(label);

		const buttonContainer = document.createElement('div');
		buttonContainer.style.cssText = `
			display: flex;
			gap: 10px;
			flex-wrap: wrap;
		`;

		const presets: { preset: TestModePreset; label: string; icon: string; color: string }[] = [
			{ preset: TestModePreset.CLASSIC, label: 'Classic', icon: '🎮', color: '#4ecdc4' },
			{ preset: TestModePreset.NIGHTMARE, label: 'Nightmare', icon: '💀', color: '#ff6b6b' },
			{ preset: TestModePreset.BATTLE_ROYALE, label: 'Battle Royale', icon: '🏆', color: '#ffd700' },
			{ preset: TestModePreset.PEACEFUL, label: 'Peaceful', icon: '🧘', color: '#95e1d3' },
		];

		presets.forEach((p) => {
			const btn = this.createPresetButton(p.icon, p.label, p.color, p.preset === this.currentPreset);
			this.presetButtons.set(p.preset, btn);

			btn.addEventListener('click', () => {
				SoundManager.getInstance().play(SoundType.UI_CLICK);
				this.loadPreset(p.preset);
			});

			buttonContainer.appendChild(btn);
		});

		section.appendChild(buttonContainer);
		return section;
	}

	/**
	 * Create action buttons (Start / Cancel)
	 */
	private createActionButtons(): HTMLDivElement {
		const section = document.createElement('div');
		section.style.cssText = `
			display: flex;
			gap: 12px;
			padding-top: 16px;
		`;

		// Cancel Button
		const cancelBtn = document.createElement('button');
		cancelBtn.textContent = 'Cancel';
		cancelBtn.style.cssText = `
			flex: 1;
			padding: 16px;
			border: 1px solid rgba(255, 255, 255, 0.1);
			border-radius: 14px;
			background: rgba(255, 255, 255, 0.05);
			color: rgba(255, 255, 255, 0.7);
			font-weight: 600;
			font-size: 16px;
			cursor: pointer;
			transition: all 0.2s ease;
		`;

		cancelBtn.addEventListener('mouseover', () => {
			cancelBtn.style.background = 'rgba(255, 255, 255, 0.1)';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		cancelBtn.addEventListener('mouseout', () => {
			cancelBtn.style.background = 'rgba(255, 255, 255, 0.05)';
		});

		cancelBtn.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.UI_CLICK);
			this.events.onCancel();
		});

		// Start Button
		const startBtn = document.createElement('button');
		startBtn.innerHTML = `<span style="margin-right: 8px">🚀</span>Start My World`;
		startBtn.style.cssText = `
			flex: 2;
			padding: 16px;
			border: none;
			border-radius: 14px;
			background: linear-gradient(135deg, #4ecdc4 0%, #2cb5e8 100%);
			color: #fff;
			font-weight: 700;
			font-size: 18px;
			cursor: pointer;
			box-shadow: 0 4px 20px rgba(78, 205, 196, 0.4);
			transition: all 0.2s ease;
		`;

		startBtn.addEventListener('mouseover', () => {
			startBtn.style.transform = 'translateY(-2px)';
			startBtn.style.boxShadow = '0 6px 25px rgba(78, 205, 196, 0.5)';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		startBtn.addEventListener('mouseout', () => {
			startBtn.style.transform = 'translateY(0)';
			startBtn.style.boxShadow = '0 4px 20px rgba(78, 205, 196, 0.4)';
		});

		startBtn.addEventListener('click', () => {
			SoundManager.getInstance().play(SoundType.GAME_START);
			this.events.onStartGame(cloneTestModeConfig(this.config));
		});

		section.appendChild(cancelBtn);
		section.appendChild(startBtn);
		return section;
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// UI COMPONENT HELPERS
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Create a section with label
	 */
	private createSection(label: string): HTMLDivElement {
		const section = document.createElement('div');
		section.style.cssText = `margin-bottom: 20px;`;

		const labelEl = document.createElement('div');
		labelEl.textContent = label.toUpperCase();
		labelEl.style.cssText = `
			font-size: 11px;
			font-weight: 700;
			color: rgba(255, 255, 255, 0.4);
			letter-spacing: 1.2px;
			margin-bottom: 10px;
		`;
		section.appendChild(labelEl);

		return section;
	}

	/**
	 * Create shape selection card
	 */
	private createShapeCard(label: string, shape: MapShape, selected: boolean): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.dataset.shape = shape;

		const icon = shape === MapShape.CIRCULAR ? '⭕' : '▢';
		btn.innerHTML = `
			<div style="font-size: 36px; margin-bottom: 6px;">${icon}</div>
			<div style="font-weight: 600; font-size: 14px;">${label}</div>
		`;

		btn.style.cssText = `
			flex: 1;
			padding: 20px 16px;
			border-radius: 16px;
			border: 2px solid ${selected ? '#4ecdc4' : 'rgba(255, 255, 255, 0.1)'};
			background: ${selected ? 'rgba(78, 205, 196, 0.15)' : 'rgba(0, 0, 0, 0.2)'};
			color: ${selected ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
			cursor: pointer;
			transition: all 0.2s ease;
			text-align: center;
		`;

		btn.addEventListener('mouseover', () => {
			if (!selected) {
				btn.style.borderColor = 'rgba(78, 205, 196, 0.5)';
				btn.style.background = 'rgba(78, 205, 196, 0.1)';
			}
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			const isSelected = this.config.mapShape === shape;
			if (!isSelected) {
				btn.style.borderColor = 'rgba(255, 255, 255, 0.1)';
				btn.style.background = 'rgba(0, 0, 0, 0.2)';
			}
		});

		return btn;
	}

	/**
	 * Create size selection card
	 */
	private createSizeCard(label: string, desc: string, size: MapSize, selected: boolean): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.dataset.size = size;

		// Size preview circles
		const previewSize = size === MapSize.SMALL ? 24 : size === MapSize.MEDIUM ? 36 : 48;

		btn.innerHTML = `
			<div style="
				width: ${previewSize}px;
				height: ${previewSize}px;
				border-radius: 50%;
				background: linear-gradient(135deg, rgba(78, 205, 196, 0.4), rgba(78, 205, 196, 0.2));
				border: 2px solid rgba(78, 205, 196, 0.5);
				margin: 0 auto 8px auto;
			"></div>
			<div style="font-weight: 600; font-size: 14px;">${label}</div>
			<div style="font-size: 11px; color: rgba(255, 255, 255, 0.4); margin-top: 4px;">${desc}</div>
		`;

		btn.style.cssText = `
			flex: 1;
			padding: 16px 12px;
			border-radius: 14px;
			border: 2px solid ${selected ? '#4ecdc4' : 'rgba(255, 255, 255, 0.1)'};
			background: ${selected ? 'rgba(78, 205, 196, 0.15)' : 'rgba(0, 0, 0, 0.2)'};
			color: ${selected ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
			cursor: pointer;
			transition: all 0.2s ease;
			text-align: center;
		`;

		btn.addEventListener('mouseover', () => {
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		return btn;
	}

	/**
	 * Update card selection state
	 */
	private updateCardSelection(btn: HTMLButtonElement, selected: boolean): void {
		btn.style.borderColor = selected ? '#4ecdc4' : 'rgba(255, 255, 255, 0.1)';
		btn.style.background = selected ? 'rgba(78, 205, 196, 0.15)' : 'rgba(0, 0, 0, 0.2)';
		btn.style.color = selected ? '#fff' : 'rgba(255, 255, 255, 0.6)';
	}

	/**
	 * Create toggle button
	 */
	private createToggleButton(label: string, desc: string, enabled: boolean): HTMLButtonElement {
		const btn = document.createElement('button');

		btn.innerHTML = `
			<div style="display: flex; align-items: center; gap: 12px;">
				<div class="toggle-indicator" style="
					width: 48px;
					height: 26px;
					border-radius: 13px;
					background: ${enabled ? '#4ecdc4' : 'rgba(255, 255, 255, 0.1)'};
					position: relative;
					transition: all 0.2s ease;
				">
					<div style="
						position: absolute;
						top: 3px;
						left: ${enabled ? '25px' : '3px'};
						width: 20px;
						height: 20px;
						border-radius: 50%;
						background: #fff;
						transition: all 0.2s ease;
						box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
					"></div>
				</div>
				<div>
					<div style="font-weight: 600; font-size: 14px; text-align: left;">${label}</div>
					<div style="font-size: 11px; color: rgba(255, 255, 255, 0.4); text-align: left;">${desc}</div>
				</div>
			</div>
		`;

		btn.style.cssText = `
			width: 100%;
			padding: 14px 16px;
			border-radius: 12px;
			border: 1px solid rgba(255, 255, 255, 0.1);
			background: rgba(0, 0, 0, 0.2);
			color: #fff;
			cursor: pointer;
			transition: all 0.2s ease;
		`;

		btn.addEventListener('mouseover', () => {
			btn.style.background = 'rgba(255, 255, 255, 0.05)';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			btn.style.background = 'rgba(0, 0, 0, 0.2)';
		});

		return btn;
	}

	/**
	 * Update toggle button state
	 */
	private updateToggleState(btn: HTMLButtonElement, enabled: boolean): void {
		const indicator = btn.querySelector('.toggle-indicator') as HTMLElement;
		if (indicator) {
			indicator.style.background = enabled ? '#4ecdc4' : 'rgba(255, 255, 255, 0.1)';
			const knob = indicator.querySelector('div') as HTMLElement;
			if (knob) {
				knob.style.left = enabled ? '25px' : '3px';
			}
		}
	}

	/**
	 * Create checkbox-style button
	 */
	private createCheckboxButton(icon: string, label: string, checked: boolean, color: string): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.dataset.powerup = label;

		btn.innerHTML = `
			<div style="display: flex; align-items: center; gap: 8px;">
				<div class="checkbox" style="
					width: 20px;
					height: 20px;
					border-radius: 6px;
					border: 2px solid ${checked ? color : 'rgba(255, 255, 255, 0.2)'};
					background: ${checked ? color : 'transparent'};
					display: flex;
					align-items: center;
					justify-content: center;
					transition: all 0.15s ease;
					font-size: 12px;
				">${checked ? '✓' : ''}</div>
				<span style="font-size: 16px;">${icon}</span>
				<span style="font-size: 13px; font-weight: 500;">${label}</span>
			</div>
		`;

		btn.style.cssText = `
			padding: 10px 14px;
			border-radius: 10px;
			border: 1px solid ${checked ? color + '40' : 'rgba(255, 255, 255, 0.1)'};
			background: ${checked ? color + '15' : 'rgba(0, 0, 0, 0.2)'};
			color: ${checked ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
			cursor: pointer;
			transition: all 0.15s ease;
		`;

		btn.addEventListener('mouseover', () => {
			btn.style.background = color + '20';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			const checkbox = btn.querySelector('.checkbox') as HTMLElement;
			const isChecked = checkbox?.textContent === '✓';
			btn.style.background = isChecked ? color + '15' : 'rgba(0, 0, 0, 0.2)';
		});

		return btn;
	}

	/**
	 * Update checkbox button state
	 */
	private updateCheckboxState(btn: HTMLButtonElement, checked: boolean): void {
		const checkbox = btn.querySelector('.checkbox') as HTMLElement;
		if (!checkbox) return;

		// Extract color from existing border
		const currentBorder = checkbox.style.borderColor;
		const color = checked ? currentBorder || '#4ecdc4' : 'rgba(255, 255, 255, 0.2)';

		checkbox.style.borderColor = color;
		checkbox.style.background = checked ? color : 'transparent';
		checkbox.textContent = checked ? '✓' : '';

		btn.style.borderColor = checked ? color + '40' : 'rgba(255, 255, 255, 0.1)';
		btn.style.background = checked ? color + '15' : 'rgba(0, 0, 0, 0.2)';
		btn.style.color = checked ? '#fff' : 'rgba(255, 255, 255, 0.6)';
	}

	/**
	 * Create food category card
	 */
	private createFoodCategoryCard(icon: string, label: string, desc: string, enabled: boolean, color: string): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.dataset.foodCategory = label;

		btn.innerHTML = `
			<div style="font-size: 32px; margin-bottom: 6px;">${icon}</div>
			<div style="font-weight: 600; font-size: 14px;">${label}</div>
			<div style="font-size: 11px; color: rgba(255, 255, 255, 0.4); margin-top: 2px;">${desc}</div>
			<div class="check-badge" style="
				position: absolute;
				top: 8px;
				right: 8px;
				width: 22px;
				height: 22px;
				border-radius: 50%;
				background: ${enabled ? color : 'rgba(255, 255, 255, 0.1)'};
				display: flex;
				align-items: center;
				justify-content: center;
				font-size: 12px;
				transition: all 0.15s ease;
			">${enabled ? '✓' : ''}</div>
		`;

		btn.style.cssText = `
			flex: 1;
			min-width: 120px;
			padding: 20px 16px;
			border-radius: 16px;
			border: 2px solid ${enabled ? color : 'rgba(255, 255, 255, 0.1)'};
			background: ${enabled ? color + '15' : 'rgba(0, 0, 0, 0.2)'};
			color: ${enabled ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
			cursor: pointer;
			transition: all 0.2s ease;
			text-align: center;
			position: relative;
		`;

		btn.addEventListener('mouseover', () => {
			btn.style.background = color + '20';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			const badge = btn.querySelector('.check-badge') as HTMLElement;
			const isEnabled = badge?.textContent === '✓';
			btn.style.background = isEnabled ? color + '15' : 'rgba(0, 0, 0, 0.2)';
		});

		return btn;
	}

	/**
	 * Update food card state
	 */
	private updateFoodCardState(btn: HTMLButtonElement, enabled: boolean): void {
		const badge = btn.querySelector('.check-badge') as HTMLElement;
		if (!badge) return;

		// Extract color from border
		const style = getComputedStyle(btn);
		const borderColor = style.borderColor;

		badge.style.background = enabled ? borderColor : 'rgba(255, 255, 255, 0.1)';
		badge.textContent = enabled ? '✓' : '';

		btn.style.borderColor = enabled ? borderColor : 'rgba(255, 255, 255, 0.1)';
		btn.style.background = enabled ? borderColor.replace('rgb', 'rgba').replace(')', ', 0.15)') : 'rgba(0, 0, 0, 0.2)';
		btn.style.color = enabled ? '#fff' : 'rgba(255, 255, 255, 0.6)';
	}

	/**
	 * Create preset button
	 */
	private createPresetButton(icon: string, label: string, color: string, selected: boolean): HTMLButtonElement {
		const btn = document.createElement('button');

		btn.innerHTML = `<span style="margin-right: 6px;">${icon}</span>${label}`;
		btn.style.cssText = `
			padding: 10px 16px;
			border-radius: 10px;
			border: 2px solid ${selected ? color : 'rgba(255, 255, 255, 0.1)'};
			background: ${selected ? color + '20' : 'rgba(0, 0, 0, 0.2)'};
			color: ${selected ? color : 'rgba(255, 255, 255, 0.6)'};
			font-weight: 600;
			font-size: 13px;
			cursor: pointer;
			transition: all 0.2s ease;
		`;

		btn.addEventListener('mouseover', () => {
			btn.style.borderColor = color;
			btn.style.background = color + '20';
			btn.style.color = color;
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			const isSelected = this.presetButtons.get(this.currentPreset) === btn;
			btn.style.borderColor = isSelected ? color : 'rgba(255, 255, 255, 0.1)';
			btn.style.background = isSelected ? color + '20' : 'rgba(0, 0, 0, 0.2)';
			btn.style.color = isSelected ? color : 'rgba(255, 255, 255, 0.6)';
		});

		return btn;
	}

	/**
	 * Create small utility button
	 */
	private createSmallButton(label: string): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.textContent = label;
		btn.style.cssText = `
			padding: 8px 14px;
			border-radius: 8px;
			border: 1px solid rgba(255, 255, 255, 0.1);
			background: rgba(0, 0, 0, 0.2);
			color: rgba(255, 255, 255, 0.6);
			font-size: 12px;
			font-weight: 500;
			cursor: pointer;
			transition: all 0.15s ease;
		`;

		btn.addEventListener('mouseover', () => {
			btn.style.background = 'rgba(255, 255, 255, 0.1)';
			btn.style.color = '#fff';
			SoundManager.getInstance().play(SoundType.UI_HOVER);
		});

		btn.addEventListener('mouseout', () => {
			btn.style.background = 'rgba(0, 0, 0, 0.2)';
			btn.style.color = 'rgba(255, 255, 255, 0.6)';
		});

		return btn;
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// STATE MANAGEMENT
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Update map shape and related UI
	 */
	private updateMapShape(shape: MapShape, circularBtn: HTMLButtonElement, rectangularBtn: HTMLButtonElement): void {
		this.config.mapShape = shape;

		// Update shape buttons
		this.updateCardSelection(circularBtn, shape === MapShape.CIRCULAR);
		this.updateCardSelection(rectangularBtn, shape === MapShape.RECTANGULAR);

		// Disable shrinking for circular maps
		if (shape === MapShape.CIRCULAR && this.config.shrinkingConfig.enabled) {
			this.config.shrinkingConfig.enabled = false;
			if (this.shrinkingToggle) {
				this.updateToggleState(this.shrinkingToggle, false);
			}
		}

		this.updateShrinkingToggleEnabled();
		this.markCustomPreset();
		this.notifyConfigChange();
	}

	/**
	 * Update shrinking toggle enabled state
	 */
	private updateShrinkingToggleEnabled(): void {
		if (!this.shrinkingToggle) return;

		const canShrink = canEnableShrinking(this.config);
		this.shrinkingToggle.style.opacity = canShrink ? '1' : '0.5';
		this.shrinkingToggle.style.cursor = canShrink ? 'pointer' : 'not-allowed';
	}

	/**
	 * Load a preset configuration
	 */
	private loadPreset(preset: TestModePreset): void {
		this.config = getTestModePreset(preset);
		this.currentPreset = preset;
		this.updateAllUI();
		this.updatePresetButtonStates();
		this.notifyConfigChange();
	}

	/**
	 * Mark current config as custom (no preset selected)
	 */
	private markCustomPreset(): void {
		this.currentPreset = TestModePreset.CUSTOM;
		this.updatePresetButtonStates();
	}

	/**
	 * Update preset button selection states
	 */
	private updatePresetButtonStates(): void {
		const presetColors: Record<TestModePreset, string> = {
			[TestModePreset.CLASSIC]: '#4ecdc4',
			[TestModePreset.NIGHTMARE]: '#ff6b6b',
			[TestModePreset.BATTLE_ROYALE]: '#ffd700',
			[TestModePreset.PEACEFUL]: '#95e1d3',
			[TestModePreset.CUSTOM]: '#888',
		};

		this.presetButtons.forEach((btn, preset) => {
			const color = presetColors[preset] || '#888';
			const isSelected = preset === this.currentPreset;

			btn.style.borderColor = isSelected ? color : 'rgba(255, 255, 255, 0.1)';
			btn.style.background = isSelected ? color + '20' : 'rgba(0, 0, 0, 0.2)';
			btn.style.color = isSelected ? color : 'rgba(255, 255, 255, 0.6)';
		});
	}

	/**
	 * Update all UI elements to match current config
	 */
	private updateAllUI(): void {
		// This would need to rebuild/update all the panels
		// For simplicity, we recreate the UI
		// In production, you'd update individual elements

		// Remove existing panels
		this.tabPanels.forEach((panel) => panel.remove());
		this.tabPanels.clear();

		// Recreate panels
		const container = this.container.querySelector('div > div:nth-child(3)') as HTMLDivElement;
		if (container) {
			this.createMapPanel(container);
			this.createNPCPanel(container);
			this.createPowerupPanel(container);
			this.createFoodPanel(container);
			this.createRatesPanel(container);
			this.createVisibilityPanel(container);
		}

		// Show active tab
		this.switchTab(this.activeTab);
	}

	/**
	 * Notify config change to event handler
	 */
	private notifyConfigChange(): void {
		this.events.onConfigChange(cloneTestModeConfig(this.config));
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// PUBLIC API
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Get the container element
	 */
	getElement(): HTMLElement {
		return this.container;
	}

	/**
	 * Show the selector
	 */
	show(): void {
		this.container.style.display = 'flex';
	}

	/**
	 * Hide the selector
	 */
	hide(): void {
		this.container.style.display = 'none';
	}

	/**
	 * Get current configuration
	 */
	getConfig(): TestModeConfig {
		return cloneTestModeConfig(this.config);
	}

	/**
	 * Set configuration
	 */
	setConfig(config: TestModeConfig): void {
		this.config = cloneTestModeConfig(config);
		this.currentPreset = TestModePreset.CUSTOM;
		this.updateAllUI();
		this.updatePresetButtonStates();
	}

	/**
	 * Cleanup resources
	 */
	destroy(): void {
		this.container.remove();
	}
}
